/**
 * Deposit return — the move-out tab's settlement section.
 *
 * Replaces the inert "Deposit return summary" email row, which drew a
 * sent/pending pill and nothing else: no amount, no deductions, no action, and
 * a hardcoded null timestamp so it could never show when anything happened.
 *
 * Seven states, and the unhappy ones are why this is a section rather than a
 * button: a missing protocol PDF, a deposit that was never paid in full, a claim
 * larger than the deposit held, and a decision that outran its own timestamps.
 *
 * Actions arrive per state — prepare, open, send, resend, settle, mark paid — and
 * are all gated on `canManage`, so a viewer who may not drive the move-out sees
 * the state without the controls.
 *
 * A missing protocol document row offers "Generate" — the tenant flow builds
 * and uploads this PDF automatically on submit, but a closed tab or a
 * transient error can leave it stuck without one forever otherwise. The
 * generate/view/download callbacks reuse the same builder and finalize path
 * the tenant flow and the standalone protocol detail page already use.
 *
 * Money shown here is the deposit ACTUALLY HELD, never 2x current rent — the
 * deposit is fixed at signing (#767) and a later rent change must not move it.
 */

import { useState, type ReactNode } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDate, formatDateTime, formatEUR } from "@/lib/v2-format";
import {
  AlertIcon,
  CheckIcon,
  Chevron,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
  FolderIcon,
  InfoIcon,
  SectionIcon,
  uppLabel,
} from "./tenant-profile-ui";
import { DepositRefundForm } from "./DepositRefundForm";
import type { DepositReleaseLogEvent } from "@/lib/depositReleaseApi";
import {
  computeRefund,
  resolveBundleReadiness,
  resolveDepositReturnState,
  resolveShortfall,
  shouldAttachBundle,
  type BundleDocKind,
  type DeductionCategory,
  type DepositReturnState,
  type SettlementRow,
} from "@/lib/depositSettlement";
import type { DepositDeductionRow } from "@/hooks/v2/useDepositSettlement";

type T = (key: string, fallback: string) => string;

const centsToEUR = (c: number | null | undefined) =>
  formatEUR((c ?? 0) / 100) ?? "€0.00";

function docLabel(t: T, kind: BundleDocKind): string {
  switch (kind) {
    case "contract":
      return t("admin.deposit.doc.contract", "Rental contract");
    case "move_in_protocol":
      return t("admin.deposit.doc.moveInProtocol", "Move-in protocol");
    case "move_out_protocol":
      return t("admin.deposit.doc.moveOutProtocol", "Move-out protocol");
    default:
      return kind;
  }
}

function categoryLabel(t: T, category: DeductionCategory): string {
  switch (category) {
    case "damage":
      return t("admin.deposit.category.damage", "Damage");
    case "cleaning":
      return t("admin.deposit.category.cleaning", "Cleaning");
    case "unpaid_rent":
      return t("admin.deposit.category.unpaidRent", "Outstanding rent");
    case "fees":
      return t("admin.deposit.category.fees", "Fees");
    case "other":
      return t("admin.deposit.category.other", "Other");
    default:
      return category;
  }
}

const CATEGORY_STYLE: Record<DeductionCategory, { bg: string; fg: string }> = {
  damage: { bg: "#FEE2E2", fg: "#B91C1C" },
  cleaning: { bg: "#E0E7FF", fg: "#4338CA" },
  unpaid_rent: { bg: "#FFEDD5", fg: "#C2410C" },
  fees: { bg: "#F1F5F9", fg: "#475569" },
  other: { bg: "#F1F5F9", fg: "#475569" },
};

const STATE_HEADER: Record<
  Exclude<DepositReturnState, "not_applicable">,
  {
    title: string;
    icon: (props: { size?: number }) => ReactNode;
    bg: string;
    fg: string;
    pill: string;
  }
> = {
  scheduled: {
    title: "Move-out bundle scheduled",
    icon: FolderIcon,
    bg: "#EEF2FF",
    fg: "#4338CA",
    // "Review" rather than "Scheduled": the chip names the phase of the deposit
    // this card is in — the condition still has to be reviewed before anything
    // is refunded. WHEN the bundle goes out is the sentence inside the card.
    pill: "Review",
  },
  blocked: {
    title: "Bundle cannot be sent",
    icon: AlertIcon,
    bg: "#FEF2F2",
    fg: "#B91C1C",
    pill: "Blocked",
  },
  prepared: {
    title: "Move-out bundle ready",
    icon: FolderIcon,
    bg: "#E0F2FE",
    fg: "#0369A1",
    pill: "Not sent",
  },
  awaiting_review: {
    title: "Move-out bundle sent",
    icon: CheckIcon,
    bg: "#DCFCE7",
    fg: "#15803D",
    pill: "Awaiting review",
  },
  released_unpaid: {
    title: "Deposit released",
    icon: CheckIcon,
    bg: "#DCFCE7",
    fg: "#15803D",
    pill: "Documented",
  },
  settled: {
    title: "Deposit settled",
    icon: CheckIcon,
    bg: "#DCFCE7",
    fg: "#15803D",
    pill: "Closed",
  },
};

const PILL_STYLE: Record<string, { bg: string; fg: string }> = {
  // Also the fallback for any label not listed here — see Pill.
  Review: { bg: "#F1F5F9", fg: "#64748B" },
  "Not started": { bg: "#F1F5F9", fg: "#94A3B8" },
  Blocked: { bg: "#FEE2E2", fg: "#B91C1C" },
  Ready: { bg: "#DCFCE7", fg: "#15803D" },
  Missing: { bg: "#FEE2E2", fg: "#B91C1C" },
  "Not sent": { bg: "#E0F2FE", fg: "#0369A1" },
  "Awaiting review": { bg: "#E0F2FE", fg: "#0369A1" },
  "Refund unpaid": { bg: "#FEF3C7", fg: "#92400E" },
  Closed: { bg: "#DCFCE7", fg: "#15803D" },
  Documented: { bg: "#DCFCE7", fg: "#15803D" },
};

/**
 * `styleKey` is the untranslated (English) status key used purely to look up
 * the pill's colour in `PILL_STYLE` — it never renders. `label` is the
 * (possibly translated) text shown to the admin. Defaulting `styleKey` to
 * `label` keeps existing call sites, which pass only the English label,
 * working unchanged.
 */
function Pill({ label, styleKey }: { label: string; styleKey?: string }) {
  const s = PILL_STYLE[styleKey ?? label] ?? PILL_STYLE.Review;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 999,
        padding: "4px 10px",
        background: s.bg,
        color: s.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function Note({
  tone,
  children,
}: {
  tone: "warn" | "info" | "bad";
  children: React.ReactNode;
}) {
  const palette = {
    warn: { bg: "#FFFBEB", border: "#FDE68A", fg: "#92400E" },
    info: { bg: "#F0F9FF", border: "#BAE6FD", fg: "#075985" },
    bad: { bg: "#FEF2F2", border: "#FECACA", fg: "#B91C1C" },
  }[tone];
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        borderRadius: 13,
        padding: "12px 14px",
        fontSize: 12.5,
        fontWeight: 500,
        lineHeight: 1.5,
        marginTop: 12,
      }}
    >
      {children}
    </div>
  );
}

/** A live action. `primary` is the one the state is waiting on. */
function ActionButton({
  label,
  onClick,
  busy = false,
  primary = false,
}: {
  label: string;
  onClick: () => void;
  busy?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        border: `1px solid ${primary ? "#0F5C52" : "#E7E9EC"}`,
        background: busy ? "#F6F7F9" : primary ? "#0F5C52" : "#fff",
        color: busy ? "#94A3B8" : primary ? "#fff" : "#6D28D9",
        borderRadius: 11,
        padding: "10px 16px",
        fontSize: 13.5,
        fontWeight: 700,
        cursor: busy ? "wait" : "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

const linkBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  color: "#0F5C52",
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
  padding: 0,
  whiteSpace: "nowrap",
};

function DocRow({
  index,
  label,
  ready,
  onView,
  onDownload,
  onGenerate,
  generating = false,
}: {
  index: number;
  label: string;
  ready: boolean;
  /** View/download only apply once the document exists. */
  onView?: () => void;
  onDownload?: () => void;
  /** Regenerate — only offered for a missing protocol, never the contract. */
  onGenerate?: () => void;
  generating?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        background: ready ? "#F8FAFB" : "#FEF2F2",
        border: `1px solid ${ready ? "#ECEEF1" : "#FECACA"}`,
        borderRadius: 12,
        padding: "10px 13px",
        fontSize: 13,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 6,
          background: ready ? "#0F5C52" : "#B91C1C",
          color: "#fff",
          fontSize: 11,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {index}
      </span>
      <span style={{ flex: 1, fontWeight: 600, color: "#0F172A" }}>
        {label}
      </span>
      {ready && onView && (
        <button type="button" onClick={onView} style={linkBtn}>
          {t("admin.documents.view", "View")}
        </button>
      )}
      {ready && onDownload && (
        <button type="button" onClick={onDownload} style={linkBtn}>
          {t("common.download", "Download")}
        </button>
      )}
      {!ready && onGenerate && (
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating}
          title={t(
            "admin.deposit.doc.generateTitle",
            "Rebuilds this protocol's PDF from its photos, inventory and signature — the tenant's submission normally does this on its own; use this only when that didn't happen."
          )}
          style={{
            ...linkBtn,
            color: generating ? "#94A3B8" : "#0F5C52",
            cursor: generating ? "wait" : "pointer",
          }}
        >
          {generating
            ? t("admin.deposit.doc.generating", "Generating…")
            : t("admin.deposit.doc.generate", "Generate")}
        </button>
      )}
      <Pill
        label={
          ready
            ? t("admin.deposit.pill.ready", "Ready")
            : t("admin.deposit.pill.missing", "Missing")
        }
        styleKey={ready ? "Ready" : "Missing"}
      />
    </div>
  );
}

function MathRow({
  label,
  value,
  tone,
  total,
}: {
  label: string;
  value: string;
  tone?: "deduct";
  total?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        fontSize: total ? 15 : 13,
        borderTop: total ? "1px solid #E2E7EC" : undefined,
        paddingTop: total ? 10 : undefined,
      }}
    >
      <span
        style={{
          color: total ? "#0F172A" : "#64748B",
          fontWeight: 700,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          fontSize: total ? 21 : undefined,
          letterSpacing: total ? "-0.02em" : undefined,
          color: total ? "#0F5C52" : tone === "deduct" ? "#B91C1C" : "#0F172A",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function DepositReturnCard({
  /**
   * Has the move-out date been confirmed? Below that the deposit sequence has
   * not begun, and the card renders a dormant placeholder rather than its
   * working states.
   */
  moveOutDateConfirmed,
  /**
   * The journey gate the booking is stopped at, from `journey.blockedBy`. Shown
   * in the dormant state so an admin can see WHY the deposit sequence has not
   * started — previously this card rendered nothing at all, which was
   * indistinguishable from the feature being absent or broken.
   */
  blockedRequirement,
  /** Effective move-out date (actual, else planned) — drives the send date. */
  moveOutDate,
  settlement,
  deductions,
  /** False when this database has not had the settlement migration applied. */
  schemaReady = true,
  /** bookings.signed_contract_url or contract_pdf_url. */
  contractUrl,
  moveInProtocolPdfPath,
  moveOutProtocolPdfPath,
  /** Deposit actually paid in, from resolveDepositState().paidCents. */
  depositHeldCents,
  /** Contractual deposit, from getExpectedDepositEUR — for the shortfall note. */
  depositExpectedCents,
  /** Assemble (or re-assemble) the bundle. Undefined = read-only. */
  onAssemble,
  /** Open the stored bundle in a new tab via a signed URL. */
  onOpenBundle,
  /** Save the stored bundle. Omitted = the save control is not rendered. */
  onDownloadBundle,
  /** Refetch the profile once the inline panel records a decision. */
  onReleased,
  /** Told what the inline panel's action actually did, for the caller to audit-log. */
  onLog,
  /** Open the email preview before sending to the representative. */
  onSendToOwner,
  /** Record a decision the representative gave by phone. */
  onSettle,
  /** Attest that the refund transfer has gone out. */
  onMarkRefundPaid,
  /** Open the stored refund statement. */
  onOpenStatement,
  /** Regenerate a protocol's PDF when the tenant-side finalize never ran. */
  onGenerateProtocolPdf,
  onOpenProtocolPdf,
  onDownloadProtocolPdf,
  generatingProtocol = null,
  markingPaid = false,
  assembling = false,
}: {
  moveOutDateConfirmed: boolean;
  blockedRequirement?: string | null;
  moveOutDate?: string | null;
  settlement?: (SettlementRow & { id: string }) | null;
  deductions?: DepositDeductionRow[];
  schemaReady?: boolean;
  contractUrl?: string | null;
  moveInProtocolPdfPath?: string | null;
  moveOutProtocolPdfPath?: string | null;
  depositHeldCents: number;
  depositExpectedCents: number;
  onAssemble?: () => void;
  onOpenBundle?: () => void;
  onDownloadBundle?: () => void;
  onReleased?: () => void;
  onLog?: (event: DepositReleaseLogEvent) => void;
  /** Opens the preview for the owner's completion notice. Settled state only. */
  onSendToOwner?: () => void;
  onSettle?: () => void;
  onMarkRefundPaid?: () => void;
  onOpenStatement?: () => void;
  onGenerateProtocolPdf?: (kind: "move_in" | "move_out") => void;
  onOpenProtocolPdf?: (kind: "move_in" | "move_out") => void;
  onDownloadProtocolPdf?: (kind: "move_in" | "move_out") => void;
  generatingProtocol?: "move_in" | "move_out" | null;
  markingPaid?: boolean;
  assembling?: boolean;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const bundleGeneratedAt = settlement?.bundle_generated_at ?? null;

  const readiness = resolveBundleReadiness({
    contractUrl,
    moveInProtocolPdfPath,
    moveOutProtocolPdfPath,
  });

  const state = resolveDepositReturnState({
    moveOutDateConfirmed,
    settlement,
    readiness,
  });

  /**
   * Dormant state. The deposit sequence genuinely has not started — every
   * action here depends on a confirmed move-out date — so nothing is offered.
   * But rendering NOTHING was the wrong way to say that: on a tenant still
   * short of the gate the whole feature vanished, which reads as missing or
   * broken rather than as not-yet-applicable. This states the position and
   * names the gate; it enables nothing.
   */
  if (state === "not_applicable") {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ECEEF1",
          borderRadius: 20,
          padding: "18px 22px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <SectionIcon bg="#F1F5F9" color="#94A3B8" size={40}>
            <FolderIcon />
          </SectionIcon>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#64748B" }}>
              {t("admin.deposit.title", "Deposit refund")}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 2,
              }}
            >
              {blockedRequirement
                ? `${t("admin.deposit.notStarted.waitingPrefix", "Starts once the move-out date is confirmed. Waiting on:")} ${blockedRequirement}.`
                : t(
                    "admin.deposit.notStarted.plain",
                    "Starts once the move-out date is confirmed."
                  )}
            </div>
          </div>
          <Pill
            label={t("admin.deposit.pill.notStarted", "Not started")}
            styleKey="Not started"
          />
        </div>
      </div>
    );
  }

  // Before release the settlement holds no snapshot, so the live deposit state
  // is what to show. After release the snapshot is the truth — it is what the
  // representative decided against, and what the tenant's statement says.
  // A settlement released without its snapshot written would otherwise report
  // "Deposit held €0.00" beside a four-hundred-euro refund — a missing snapshot
  // reading as a deposit that was never paid. Fall back to the live figure.
  const heldCents = settlement?.released_at
    ? (settlement.deposit_held_cents ?? depositHeldCents)
    : depositHeldCents;
  const expectedCents = settlement?.released_at
    ? (settlement.deposit_expected_cents ?? 0)
    : depositExpectedCents;

  const lines = deductions ?? [];
  const refund = computeRefund({ heldCents, deductions: lines });
  const shortfall = resolveShortfall({ heldCents, expectedCents });
  const header = STATE_HEADER[state];

  const sendDate = (() => {
    if (!moveOutDate) return null;
    const d = new Date(moveOutDate);
    if (!Number.isFinite(d.getTime())) return null;
    // The move-out protocol is submitted on the day itself, so the bundle goes
    // out the morning after.
    d.setDate(d.getDate() + 1);
    return formatDate(d.toISOString().slice(0, 10));
  })();

  const subtitle = (() => {
    switch (state) {
      case "scheduled":
        return sendDate
          ? `${t("admin.deposit.subtitle.scheduledWithDatePrefix", "Bundle goes to the representative on")} ${sendDate} ${t("admin.deposit.subtitle.scheduledWithDateSuffix", "— the day after move-out.")}`
          : t(
              "admin.deposit.subtitle.scheduledNoDate",
              "Bundle goes to the representative the day after move-out."
            );
      case "blocked":
        return (
          settlement?.bundle_blocked_reason ||
          `${t("admin.deposit.subtitle.missingPrefix", "Missing:")} ${readiness.missing.map((k) => docLabel(t, k)).join(", ")}.`
        );
      case "prepared": {
        const built = formatDateTime(settlement?.bundle_generated_at ?? null);
        const pages = settlement?.bundle_page_count;
        const size = settlement?.bundle_size_bytes;
        const detail = [
          pages ? `${pages} ${t("admin.deposit.pages", "pages")}` : null,
          size ? `${(size / (1024 * 1024)).toFixed(1)} MB` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return [
          built
            ? `${t("admin.deposit.subtitle.assembledPrefix", "Assembled")} ${built}`
            : t("admin.deposit.subtitle.assembledPrefix", "Assembled"),
          detail,
        ]
          .filter(Boolean)
          .join(" · ");
      }
      case "awaiting_review": {
        const sent = formatDateTime(settlement?.bundle_sent_at ?? null);
        const to =
          settlement?.bundle_recipient_name ||
          settlement?.bundle_recipient_email ||
          t("admin.deposit.theRepresentative", "the representative");
        return sent
          ? `${sent} · ${t("admin.deposit.subtitle.toPrefix", "to")} ${to}`
          : `${t("admin.deposit.subtitle.sentToPrefix", "Sent to")} ${to}`;
      }
      case "released_unpaid":
        return `${formatDate(settlement?.released_at ?? null) ?? t("admin.deposit.subtitle.released", "Released")} · ${t("admin.deposit.subtitle.transferNotMarkedPaid", "transfer to the tenant not yet marked paid.")}`;
      case "settled": {
        const paid = formatDate(settlement?.refund_paid_at ?? null);
        const withheld = centsToEUR(refund.deductionsCents);
        return `${centsToEUR(settlement?.refund_cents ?? refund.refundCents)} ${t("admin.deposit.subtitle.refunded", "refunded")}${paid ? ` ${paid}` : ""}${
          lines.length
            ? ` · ${withheld} ${t("admin.deposit.subtitle.withheldAcross", "withheld across")} ${lines.length} ${
                lines.length === 1
                  ? t("admin.deposit.itemSingular", "item")
                  : t("admin.deposit.itemsPlural", "items")
              }`
            : ""
        }.`;
      }
      default:
        return "";
    }
  })();

  /**
   * Is the compact refund form on screen?
   *
   * When it is, the section is the mockup's: header, the bundle as a document,
   * then the form. Everything that would otherwise sit between them restates
   * what the form already says — the stage sentence, the held/refund maths, an
   * "Open bundle" beside a document row that opens it, a "Settle deposit…" that
   * opens a dialog onto this very decision. Two ways to read the same number,
   * stacked, is how an admin ends up trusting the wrong one.
   *
   * Offered from `prepared` as well as `awaiting_review`. Where Coliviq IS the
   * representative, sending the bundle means emailing ourselves and waiting for
   * our own link before the form appears — a step that exists only to satisfy the
   * state machine. The moment the bundle is assembled there is a document to
   * decide against, which is the only thing the decision actually needs.
   *
   * Nothing downstream chases a deposit released this way: prepareBundleForBooking
   * answers `already_released`, sendBundleForSettlement refuses outright, and the
   * reminder and escalation jobs filter on `released_at IS NULL`. So an unsent
   * bundle cannot later go out asking someone to review a refund already paid.
   */
  const inlineRefund =
    (state === "awaiting_review" || state === "prepared") &&
    !!onSettle &&
    !!settlement?.id;

  // Before assembly the maths would only restate the deposit card; from the
  // moment a bundle exists the numbers are what someone will act on — unless
  // the form is showing them live, a line below.
  const showMath =
    state !== "scheduled" &&
    state !== "blocked" &&
    state !== "prepared" &&
    !inlineRefund;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ECEEF1",
        borderRadius: 20,
        padding: "18px 22px",
      }}
    >
      {/*
        The section header is fixed — "Deposit refund", with the same one-line
        description at every stage — so the section is recognisable in the tab
        regardless of how far along it is. The stage still shows, in the pill
        and in the per-state line inside; it just no longer renames the section
        under the reader.
      */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <SectionIcon bg={header.bg} color={header.fg} size={40}>
          <header.icon />
        </SectionIcon>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {t("admin.deposit.title", "Deposit refund")}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "#94A3B8",
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            {t(
              "admin.deposit.subheading",
              "Compare move-in and move-out condition, then record the refund"
            )}
          </div>
        </div>
        <Pill
          label={t(`admin.deposit.state.${state}.pill`, header.pill)}
          styleKey={header.pill}
        />
        <button
          type="button"
          onClick={() => setShowGuide((v) => !v)}
          aria-expanded={showGuide}
          aria-label={
            showGuide
              ? t("admin.deposit.hideHowThisWorks", "Hide how this works")
              : t("admin.deposit.showHowThisWorks", "Show how this works")
          }
          title={t("admin.deposit.howThisWorks", "How this works")}
          style={{
            background: showGuide ? "#F1F5F9" : "none",
            border: "none",
            borderRadius: 999,
            cursor: "pointer",
            width: 22,
            height: 22,
            color: "#94A3B8",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          <InfoIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={
            open
              ? t("admin.deposit.collapse", "Collapse deposit refund")
              : t("admin.deposit.expand", "Expand deposit refund")
          }
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
            display: "inline-flex",
            lineHeight: 1,
          }}
        >
          <Chevron open={open} size={18} />
        </button>
      </div>

      {showGuide && (
        <ol
          style={{
            margin: "12px 0 0 0",
            padding: "12px 14px 12px 30px",
            background: "#F8FAFC",
            border: "1px solid #ECEEF1",
            borderRadius: 12,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "#475569",
          }}
        >
          <li>
            {t(
              "admin.deposit.guide.step1",
              "The day after move-out, we merge the contract, move-in and move-out protocols into one PDF automatically."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step2",
              "That PDF is emailed to the lessor's representative to review."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step3",
              "They decide the refund — full, partial with an itemised reason per deduction, or none — from a link in that email, no account needed."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step4",
              'Where coliviq is the representative, the refund is recorded here instead: type the amount, attach the bank confirmation for the transfer, confirm. That closes the deposit in one step — no separate "mark paid" — and moves the tenant to Moved out.'
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step5",
              "Once decided, a refund statement is emailed to the tenant and the representative, the apartment's owner is told the refund has been paid, and this card updates to match."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step6",
              'No decision after 7 days: a reminder email goes to the representative ("you still haven\'t reviewed this").'
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step7",
              "No decision after 14 days: escalates to the coliviq admin team instead of the representative."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step8",
              "Decision made but the refund not marked paid after 7 days: nags the admin team to follow up."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step9",
              "If sending the statement ever fails the first time, it retries automatically the next day."
            )}
          </li>
          <li>
            {t(
              "admin.deposit.guide.step10",
              'If a move-in or move-out document row below says "Missing" with a "Generate" link — the tenant\'s submission normally produces that PDF on its own; this only shows up if that step didn\'t finish, and "Generate" builds it here instead of waiting for a retry.'
            )}
          </li>
        </ol>
      )}

      {open && (
        <>
          {/* The stage sentence the header used to carry. */}
          {!inlineRefund && (
            <div
              style={{
                fontSize: 12.5,
                color: "#64748B",
                fontWeight: 500,
                marginTop: 12,
              }}
            >
              <b style={{ color: "#0F172A" }}>
                {t(`admin.deposit.state.${state}.title`, header.title)}
              </b>{" "}
              — <span>{subtitle}</span>
            </div>
          )}

          {/*
        The assembled bundle as a document row, mirroring how every other
        move-out document is presented in this tab. Only once it exists —
        before assembly there is no file to open.
      */}
          {onOpenBundle &&
            (state === "prepared" ||
              !!settlement?.bundle_sent_at ||
              !!settlement?.released_at) && (
              <div style={{ marginTop: 16 }}>
                <div style={{ ...uppLabel, marginBottom: 8 }}>
                  {t(
                    "admin.deposit.moveOutDocumentation",
                    "Move-out documentation"
                  )}
                </div>
                <BundleFileRow
                  generatedAt={bundleGeneratedAt}
                  onOpen={onOpenBundle}
                  onDownload={onDownloadBundle}
                />
              </div>
            )}

          {/*
            Record the refund without leaving the tab, directly under the
            document it is a decision about. Writes through the same endpoint as
            the "Settle deposit…" dialog and the representative's token page, so
            there is one implementation of the refund maths.

            Only while the decision is still open: once released the settlement
            is immutable (the release endpoint answers 409) and the recorded
            summary further down is what there is to show.
          */}
          {inlineRefund && settlement?.id && (
            <div
              style={{
                marginTop: 18,
                paddingTop: 18,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              {/*
                Recording the refund off an UNSENT bundle is right when we are the
                representative and wrong when the lessor has their own — in which
                case this would decide, in their name, on money they are entitled
                to rule on. So the state is stated rather than assumed, and the
                address the bundle would go to is named: an admin can see at a
                glance whether that is us. Naming the address beats inferring from
                lessors.is_platform, which would be a guess dressed as a fact.
              */}
              {state === "prepared" && (
                <Note tone="info">
                  <b>
                    {t(
                      "admin.deposit.note.bundleNotSentBold",
                      "The bundle has not been sent."
                    )}
                  </b>{" "}
                  {t(
                    "admin.deposit.note.bundleNotSentBody",
                    "Record the refund here when coliviq is the representative for this apartment — we hold the deposit, so there is nobody else to wait for."
                  )}{" "}
                  {settlement.bundle_recipient_email
                    ? `${t("admin.deposit.note.otherwiseSendToPrefix", "Otherwise send it to")} ${settlement.bundle_recipient_email} ${t("admin.deposit.note.otherwiseSendToSuffix", "first and let them decide.")}`
                    : t(
                        "admin.deposit.note.otherwiseSendToRep",
                        "Otherwise send it to the lessor's representative first and let them decide."
                      )}
                </Note>
              )}
              <DepositRefundForm
                auth={{ mode: "admin", settlementId: settlement.id }}
                depositHeldCents={heldCents}
                onReleased={onReleased}
                onLog={onLog}
              />
            </div>
          )}

          {/* Which documents the bundle needs, and which are ready. */}
          {(state === "scheduled" || state === "blocked") && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 12,
              }}
            >
              {readiness.docs.map((d, i) => {
                const protocolKind =
                  d.kind === "move_in_protocol"
                    ? "move_in"
                    : d.kind === "move_out_protocol"
                      ? "move_out"
                      : null;
                return (
                  <DocRow
                    key={d.kind}
                    index={i + 1}
                    label={docLabel(t, d.kind)}
                    ready={d.ready}
                    onView={
                      protocolKind && onOpenProtocolPdf
                        ? () => onOpenProtocolPdf(protocolKind)
                        : undefined
                    }
                    onDownload={
                      protocolKind && onDownloadProtocolPdf
                        ? () => onDownloadProtocolPdf(protocolKind)
                        : undefined
                    }
                    onGenerate={
                      protocolKind && onGenerateProtocolPdf
                        ? () => onGenerateProtocolPdf(protocolKind)
                        : undefined
                    }
                    generating={
                      !!protocolKind && generatingProtocol === protocolKind
                    }
                  />
                );
              })}
            </div>
          )}

          {state === "blocked" && (
            <Note tone="warn">
              <b>
                {t(
                  "admin.deposit.note.blockedBold",
                  "The bundle will not send while a document is missing."
                )}
              </b>{" "}
              {t(
                "admin.deposit.note.blockedBody",
                "A partial bundle would let the representative decide on the deposit without seeing the walkthrough it depends on. The send retries each morning."
              )}
            </Note>
          )}

          {/* Deduction lines, as they will print in the tenant's statement. */}
          {lines.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {lines.map((d) => {
                const cat = CATEGORY_STYLE[d.category] ?? CATEGORY_STYLE.other;
                return (
                  <div
                    key={d.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "148px 1fr 108px",
                      gap: 12,
                      alignItems: "start",
                      padding: "13px 0",
                      borderTop: "1px solid #F1F3F5",
                    }}
                  >
                    <span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 8,
                          padding: "5px 9px",
                          background: cat.bg,
                          color: cat.fg,
                          display: "inline-block",
                        }}
                      >
                        {categoryLabel(t, d.category)}
                      </span>
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: "#334155",
                        fontWeight: 500,
                      }}
                    >
                      {d.reason}
                      {d.evidence_paths?.length > 0 && (
                        <span
                          style={{
                            display: "inline-block",
                            marginTop: 6,
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: "#6D28D9",
                            background: "#F7F5FF",
                            border: "1px solid #ECE7FD",
                            borderRadius: 7,
                            padding: "3px 8px",
                          }}
                        >
                          {d.evidence_paths.length}{" "}
                          {d.evidence_paths.length === 1
                            ? t(
                                "admin.deposit.attachmentSingular",
                                "attachment"
                              )
                            : t(
                                "admin.deposit.attachmentsPlural",
                                "attachments"
                              )}
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        textAlign: "right",
                        fontSize: 14,
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color: "#B91C1C",
                      }}
                    >
                      −{centsToEUR(d.amount_cents)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {showMath && (
            <div
              style={{
                background: "#F8FAFB",
                border: "1px solid #ECEEF1",
                borderRadius: 14,
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 9,
                marginTop: 14,
              }}
            >
              <MathRow
                label={t("admin.deposit.math.depositHeld", "Deposit held")}
                value={centsToEUR(heldCents)}
              />
              {lines.length > 0 && (
                <MathRow
                  label={`${t("admin.deposit.math.deductions", "Deductions")} · ${lines.length} ${
                    lines.length === 1
                      ? t("admin.deposit.itemSingular", "item")
                      : t("admin.deposit.itemsPlural", "items")
                  }`}
                  value={`−${centsToEUR(refund.deductionsCents)}`}
                  tone="deduct"
                />
              )}
              <MathRow
                label={t(
                  "admin.deposit.math.refundToTenant",
                  "Refund to tenant"
                )}
                value={centsToEUR(
                  settlement?.released_at
                    ? (settlement.refund_cents ?? refund.refundCents)
                    : refund.refundCents
                )}
                total
              />
            </div>
          )}

          {shortfall.hasShortfall && (
            <Note tone="warn">
              <b>
                {t("admin.deposit.note.heldOfPrefix", "Held")}{" "}
                {centsToEUR(heldCents)}{" "}
                {t("admin.deposit.note.heldOfMiddle", "of")}{" "}
                {centsToEUR(expectedCents)}.
              </b>{" "}
              {t(
                "admin.deposit.note.shortfallBodyPrefix",
                "The tenant never paid the deposit in full, so only"
              )}{" "}
              {centsToEUR(heldCents)}{" "}
              {t(
                "admin.deposit.note.shortfallBodyMiddle",
                "can be released — the"
              )}{" "}
              {centsToEUR(shortfall.shortfallCents)}{" "}
              {t(
                "admin.deposit.note.shortfallBodySuffix",
                "shortfall is a separate receivable, not a deduction."
              )}
            </Note>
          )}

          {refund.overDeducted && (
            <Note tone="bad">
              <b>
                {centsToEUR(refund.excessCents)}{" "}
                {t(
                  "admin.deposit.note.aboveDepositHeld",
                  "above the deposit held."
                )}
              </b>{" "}
              {t(
                "admin.deposit.note.overDeductedBody",
                "The refund floors at zero; the excess is raised as a claim rather than netted off silently."
              )}
            </Note>
          )}

          {state === "awaiting_review" &&
            settlement?.bundle_size_bytes != null &&
            !shouldAttachBundle(settlement.bundle_size_bytes) && (
              <Note tone="info">
                {t(
                  "admin.deposit.note.deliveredAsLink",
                  "Delivered as a signed link rather than an attachment — the bundle is over the size receiving mail servers reliably accept."
                )}
              </Note>
            )}

          {/* Names the party that is actually missing it. `statement_sent_at` now
          means "the tenant has it" specifically, so a statement can have reached
          the representative and still be undelivered where it matters. */}
          {(state === "released_unpaid" || state === "settled") &&
            !settlement?.statement_tenant_sent_at && (
              <Note tone="warn">
                <b>
                  {t(
                    "admin.deposit.note.statementNotReachedTenant",
                    "The refund statement has not reached the tenant."
                  )}
                </b>{" "}
                {settlement?.statement_rep_sent_at
                  ? t(
                      "admin.deposit.note.repHasCopy",
                      "The representative has their copy, but the tenant has not been told what they are owed."
                    )
                  : t(
                      "admin.deposit.note.nobodyToldYet",
                      "The decision is recorded, but nobody has been told what the tenant is owed."
                    )}{" "}
                {t(
                  "admin.deposit.note.dailyTickRetries",
                  "The daily tick retries and sends only to whoever is still missing; if it keeps failing, check the tenant has an email on file."
                )}
              </Note>
            )}

          {!schemaReady && (
            <Note tone="info">
              {t(
                "admin.deposit.note.schemaNotReadyPrefix",
                "This database has not had migration"
              )}{" "}
              <code>20260814130000_deposit_settlements</code>{" "}
              {t(
                "admin.deposit.note.schemaNotReadySuffix",
                "applied yet, so no settlement can be read or created here."
              )}
            </Note>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            {(state === "scheduled" || state === "blocked") && onAssemble && (
              <ActionButton
                label={
                  assembling
                    ? t("admin.deposit.action.preparing", "Preparing…")
                    : t("admin.deposit.action.prepareBundle", "Prepare bundle")
                }
                onClick={onAssemble}
                busy={assembling}
                primary={state === "scheduled"}
              />
            )}
            {state === "prepared" && (
              <>
                {/* Reviewing the bundle first is the point of preparing one — that
                is the file row's own open/save controls above, and repeating it
                as a button here gave the same signed URL three ways in.

                No send button lives here any more. The mail this card can send is
                the owner's completion notice, and its whole claim is that the
                money has already gone back — untrue until the refund is recorded,
                which is why it only appears once the settlement is settled. The
                pre-refund bundle still reaches the representative; the
                day-after-move-out tick sends it, unprompted. The form's own
                confirm is the single emphasised control here. */}
                {onAssemble && (
                  <ActionButton
                    label={
                      assembling
                        ? t("admin.deposit.action.rebuilding", "Rebuilding…")
                        : t("admin.deposit.action.rebuild", "Rebuild")
                    }
                    onClick={onAssemble}
                    busy={assembling}
                  />
                )}
              </>
            )}
            {state === "awaiting_review" && onSettle && !inlineRefund && (
              <ActionButton
                label={t(
                  "admin.deposit.action.settleDeposit",
                  "Settle deposit…"
                )}
                onClick={onSettle}
                primary
              />
            )}
            {state === "released_unpaid" && (
              <>
                {onMarkRefundPaid && (
                  <ActionButton
                    label={
                      markingPaid
                        ? t("admin.deposit.action.recording", "Recording…")
                        : t(
                            "admin.deposit.action.markRefundPaid",
                            "Mark refund paid"
                          )
                    }
                    onClick={onMarkRefundPaid}
                    busy={markingPaid}
                    primary
                  />
                )}
                {onOpenStatement && settlement?.statement_pdf_path && (
                  <ActionButton
                    label={t("admin.deposit.action.statement", "Statement")}
                    onClick={onOpenStatement}
                  />
                )}
              </>
            )}
            {state === "settled" && (
              <>
                {onOpenStatement && (
                  <ActionButton
                    label={t("admin.deposit.action.statement", "Statement")}
                    onClick={onOpenStatement}
                  />
                )}
                {/* A resend, not the first send: confirming the refund mails the
                owner on its own. This is the button for the address that was
                wrong, or the owner who says it never arrived. */}
                {onSendToOwner && (
                  <ActionButton
                    label={t(
                      "admin.deposit.action.sendToOwner",
                      "Send to owner…"
                    )}
                    onClick={onSendToOwner}
                  />
                )}
              </>
            )}
          </div>

          {/* What was decided, once it is decided. */}
          {settlement?.released_at && (
            <RecordedRefund
              refundCents={settlement.refund_cents ?? refund.refundCents}
              heldCents={heldCents}
              recordedAt={settlement.released_at}
              note={settlement.release_note ?? null}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * The assembled bundle, presented as a file rather than as a status.
 *
 * Two controls, two outcomes: open it in a tab, or save it. There used to be a
 * third — a "preview" button wired to the same `onOpen` handler as the tab one,
 * so two adjacent icons did the identical thing and neither told you which. A
 * duplicate control on a document an admin is about to send to a lessor is worse
 * than one fewer affordance.
 *
 * `onDownload` is optional — without a handler the save control is omitted rather
 * than rendered inert, so nothing on screen promises an action that does nothing.
 */
function BundleFileRow({
  generatedAt,
  onOpen,
  onDownload,
}: {
  /**
   * When the bundle was assembled. Nullable, and the row still renders without
   * it: this row is the ONLY way to the file, so gating it on a timestamp left a
   * sent bundle with no way in at all for anyone who could not settle it.
   */
  generatedAt: string | null;
  onOpen: () => void;
  onDownload?: () => void;
}) {
  const { t } = useLanguage();
  const iconBtn: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 9,
    border: "1px solid #E3E6EA",
    background: "#fff",
    cursor: "pointer",
    lineHeight: 1,
    color: "#64748B",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "#F8FAFC",
        border: "1px solid #F1F5F9",
        borderRadius: 12,
        padding: "12px 14px",
      }}
    >
      <SectionIcon bg="#FEF2F2" color="#B91C1C" size={34}>
        <FileTextIcon size={16} />
      </SectionIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>
          {t("admin.deposit.bundleFileName", "Deposit review.pdf")}
        </div>
        <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>
          {t("admin.deposit.bundleContractLabel", "Contract")} ·{" "}
          {t("admin.deposit.doc.moveInProtocol", "Move-in protocol")} ·{" "}
          {t("admin.deposit.doc.moveOutProtocol", "Move-out protocol")}
          {generatedAt
            ? ` · ${t("admin.deposit.generatedPrefix", "generated")} ${formatDate(generatedAt)}`
            : ""}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpen}
        style={iconBtn}
        aria-label={t(
          "admin.deposit.openBundleAria",
          "Open the bundle in a new tab"
        )}
        title={t("admin.deposit.openInNewTab", "Open in a new tab")}
      >
        <ExternalLinkIcon size={16} />
      </button>
      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          style={iconBtn}
          aria-label={t(
            "admin.deposit.downloadBundleAria",
            "Download the bundle"
          )}
          title={t("common.download", "Download")}
        >
          <DownloadIcon size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * The decision, after the fact.
 *
 * Edit is present but disabled, and says why on hover. A released deposit
 * cannot be amended: the refund statement has already been generated and sent
 * to the tenant, and the release endpoint rejects a second write with 409.
 * Hiding the control would leave that rule invisible; offering a live one would
 * promise an amendment the backend refuses.
 */
function RecordedRefund({
  refundCents,
  heldCents,
  recordedAt,
  note,
}: {
  refundCents: number;
  heldCents: number;
  recordedAt: string;
  note: string | null;
}) {
  const { t } = useLanguage();
  return (
    <div
      style={{
        marginTop: 18,
        background: "#F0FDF4",
        border: "1px solid #DCFCE7",
        borderRadius: 14,
        padding: "14px 16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ color: "#15803D", display: "inline-flex" }}>
          <CheckIcon size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#15803D" }}>
            {formatEUR(refundCents / 100)}{" "}
            {t("admin.deposit.recorded.ofPrefix", "of")}{" "}
            {formatEUR(heldCents / 100)}{" "}
            {t("admin.deposit.recorded.refunded", "refunded")}
          </div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>
            {t("admin.deposit.recorded.documented", "Documented")}{" "}
            {formatDateTime(recordedAt)}
          </div>
        </div>
        <button
          type="button"
          disabled
          title={t(
            "admin.deposit.recorded.editDisabledTitle",
            "Released deposits cannot be changed — the tenant's refund statement has already been issued."
          )}
          style={{
            border: "1px solid #E3E6EA",
            background: "#fff",
            color: "#94A3B8",
            borderRadius: 9,
            padding: "6px 14px",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: "not-allowed",
          }}
        >
          {t("admin.deposit.recorded.edit", "Edit")}
        </button>
      </div>
      {note && (
        <div
          style={{
            marginTop: 10,
            background: "#fff",
            border: "1px solid #DCFCE7",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13,
            color: "#0F172A",
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}
